import { sitApi } from './sitApiClient';

/**
 * POST /batches — server-side batch orchestration.
 *
 * `plans` is the array of v1 TestPlan objects the user approved on the Verify
 * Plans screen. `overrides` is the run-time destination/fromDid that wins over
 * each plan's target block. The backend enqueues every plan onto an SQS FIFO
 * queue (one message per plan, MessageGroupId = batchId), so dialing is
 * sequential per batch and survives browser closure / tab navigation.
 *
 * Returns: { batchId, status, totalCount, ... }.
 */
export async function createBatch({ plans, overrides, label, mode }) {
  const body = { plans };
  if (overrides && (overrides.destination || overrides.fromDid)) body.overrides = overrides;
  if (label) body.label = label;
  // `mode: 'inbrowser'` creates a batch row WITHOUT enqueueing to SQS — the
  // SPA's runBatchSequentially drives the runs and POSTs progress updates
  // back. Used whenever any plan carries metadata.preconditions so the
  // runtime gate modal can pause for engineer input. Default 'server'
  // (durable SQS-driven batch).
  if (mode) body.mode = mode;
  const { data } = await sitApi.post('/batches', body);
  return data;
}

/**
 * Report progress on an in-browser batch as each plan finalizes.
 *
 * `result` shape: { planIndex, name, testRunId?, finalStatus, startedAt?,
 *                   finishedAt?, error? }
 *
 * The backend appends-by-index (idempotent on retries), recomputes
 * `doneCount`, and flips `status` to COMPLETED when the last plan lands.
 * Locked to mode=inbrowser batches + the creator on the server side; a
 * misrouted call will 400 not corrupt anything.
 */
export async function updateBatchProgress(batchId, result) {
  const { data } = await sitApi.post(
    `/batches/${encodeURIComponent(batchId)}/progress`,
    { result }
  );
  return data;
}

export async function listBatches() {
  const { data } = await sitApi.get('/batches');
  return data.batches ?? [];
}

export async function getBatch(batchId) {
  const { data } = await sitApi.get(`/batches/${encodeURIComponent(batchId)}`);
  return data;
}

/**
 * POST /batches/{id}/cancel — flips the batch to CANCELLED. Already-enqueued
 * SQS messages stay in the queue; the batch-runner skips dispatch on dequeue
 * when the batch is CANCELLED, so messages drain naturally without taking
 * destructive PurgeQueue action that would affect other concurrent batches.
 */
export async function cancelBatch(batchId) {
  const { data } = await sitApi.post(`/batches/${encodeURIComponent(batchId)}/cancel`);
  return data;
}
